/* ==================================================================== */
/*  Zodiac glyphs as stroked line icons (currentColor, 24×24 grid) —      */
/*  same aesthetic as <Icon>, drawn instead of unicode symbols so they    */
/*  never fall back to a platform emoji and match the site's line style.   */
/* ==================================================================== */

export type ZodiacName =
  | 'Aries' | 'Taurus' | 'Gemini' | 'Cancer' | 'Leo' | 'Virgo'
  | 'Libra' | 'Scorpio' | 'Sagittarius' | 'Capricorn' | 'Aquarius' | 'Pisces'

const glyphs: Record<ZodiacName, React.ReactNode> = {
  // Ram's horns
  Aries: <path d="M8.8 10.8C7.3 9.6 7.5 6.8 9.7 7C11.2 7.2 11.6 8.9 12 15.5M15.2 10.8C16.7 9.6 16.5 6.8 14.3 7C12.8 7.2 12.4 8.9 12 15.5" />,
  // Bull: circle body + horns
  Taurus: (
    <>
      <path d="M6.5 8.5A6 5 0 0 0 17.5 8.5" />
      <circle cx="12" cy="15" r="4" />
    </>
  ),
  // The twins (II with caps)
  Gemini: (
    <>
      <path d="M7 6Q12 4.2 17 6M7 18Q12 19.8 17 18" />
      <path d="M9.6 5.4V18.6M14.4 5.4V18.6" />
    </>
  ),
  // Crab: two curled claws
  Cancer: (
    <>
      <path d="M5 9.4C10 8.4 14 9.6 14 12.6" />
      <path d="M19 14.6C14 15.6 10 14.4 10 11.4" />
      <circle cx="4.4" cy="9" r="1.9" />
      <circle cx="19.6" cy="15" r="1.9" />
    </>
  ),
  // Lion: loop + curling tail
  Leo: (
    <>
      <circle cx="9" cy="13" r="3" />
      <path d="M11.5 11C10.8 8.2 13.5 7 15.2 9.4C16.9 11.8 15.2 15.8 12.6 16.4" />
    </>
  ),
  // Maiden: M with an inward-curled leg
  Virgo: (
    <>
      <path d="M5 8V16" />
      <path d="M5 9C5 6.8 8 6.8 8 9V16" />
      <path d="M8 9C8 6.8 11 6.8 11 9V15C11 18 15 17.2 15 13" />
      <path d="M15 13C15 16 17.2 16 18.5 14.5" />
    </>
  ),
  // Scales: base line + balanced beam
  Libra: (
    <>
      <path d="M4 17.5H20" />
      <path d="M4 13.2H8.4A3.7 3.7 0 0 1 15.6 13.2H20" />
    </>
  ),
  // Scorpion: M with a barbed tail
  Scorpio: (
    <>
      <path d="M4 8V16" />
      <path d="M4 9C4 6.8 7 6.8 7 9V16" />
      <path d="M7 9C7 6.8 10 6.8 10 9V16" />
      <path d="M10 11L17.5 16.5" />
      <path d="M13.5 16.5H17.7V12.3" />
    </>
  ),
  // Archer's arrow + crossbar
  Sagittarius: (
    <>
      <path d="M6 18L16.5 7.5" />
      <path d="M16.5 7.5H11.3M16.5 7.5V12.7" />
      <path d="M8.8 11L13 15.2" />
    </>
  ),
  // Sea-goat: horns into a looped tail
  Capricorn: (
    <>
      <path d="M5 8V12.5C5 15.5 8 16 9.5 13L12.5 8C14 5.4 17 6.6 16.6 9.6C16.3 12 13.8 12 13.5 14" />
      <circle cx="15" cy="15.6" r="2.1" />
    </>
  ),
  // Water-bearer: two waves
  Aquarius: (
    <>
      <path d="M4 10.5L7 8.5L10 10.5L13 8.5L16 10.5L19 8.5" />
      <path d="M4 15.5L7 13.5L10 15.5L13 13.5L16 15.5L19 13.5" />
    </>
  ),
  // Fishes: two arcs joined by a bar
  Pisces: (
    <>
      <path d="M7.5 5C4 8 4 16 7.5 19M16.5 5C20 8 20 16 16.5 19" />
      <path d="M5 12H19" />
    </>
  ),
}

type Props = {
  name: ZodiacName
  size?: number
  className?: string
  strokeWidth?: number
}

export function ZodiacIcon({ name, size = 24, className, strokeWidth = 1.6 }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {glyphs[name]}
    </svg>
  )
}
